import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Baby, School, HeartPulse, Music, ExternalLink, TrendingUp, Users } from "lucide-react";
import { LineChart, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { dec, formatNumber } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";
import { BemanningEndring, Normbrot, Kuttkalkulator, BEMANNING_NORM } from "./BemanningEndring";
import { DelKnapp } from "./DelKnapp";

const n1 = (v) => (v == null ? "—" : dec(v, 1));

const KORT = [
  {
    key: "dekning_1_5",
    testId: "barnehagedekning",
    icon: Baby,
    title: "Barnehagedekning",
    unit: "%",
    farge: "#103766",
    forklaring: "Del av barna 1–5 år som har barnehageplass, uavhengig av om barnehagen er kommunal eller privat.",
    detalj: (r) => [
      ["Barn med plass", formatNumber(r.barnehagebarn)],
      ["1–2 år", `${n1(r.dekning_1_2)} %`],
      ["3–5 år", `${n1(r.dekning_3_5)} %`],
      ["Barn 1–5 år", formatNumber(r.barn_1_5)],
    ],
    kjelde: ["13502", "KOSTRA 13502"],
  },
  {
    key: "elevar_per_skule",
    testId: "elevar-per-skule",
    icon: School,
    title: "Barn per skule",
    unit: "elevar",
    farge: "#3F8A86",
    forklaring: "Elevar i kommunale grunnskular delt på talet på skular. Mange elevar per skule tyder større skular og færre grendeskular.",
    detalj: (r) => [
      ["Elevar", formatNumber(r.elevar)],
      ["Skular", formatNumber(r.skular)],
      ["Barn 6–15 år", r.barn_6_15 != null ? formatNumber(r.barn_6_15) : "—"],
    ],
    kjelde: ["11971", "SSB 11971"],
  },
  {
    key: "heildogn_per_100_eldre",
    testId: "sjukeheimsplassar",
    icon: HeartPulse,
    title: "Sjukeheim og omsorgsbustad per 100 over 80 år",
    unit: "plassar",
    farge: "#C0392B",
    forklaring:
      "Disponible sjukeheimsplassar pluss bebuarar i bustader kommunen disponerer til pleie- og omsorgsformål, målt mot innbyggjarar 80 år og over.",
    detalj: (r) => [
      ["Sjukeheimsplassar", formatNumber(r.sjukeheimsplassar)],
      ["Omsorgsbustader", formatNumber(r.omsorgsbustader)],
      ["Innbyggjarar 80+", formatNumber(r.eldre_80)],
      ["Berre sjukeheim per 100", n1(r.sjukeheim_per_100_eldre)],
      ["Berre omsorgsbustad per 100", n1(r.omsorgsbustad_per_100_eldre)],
      ["Av dei i bustad er 80+", r.omsorgsbustad_andel_80 == null ? "—" : `${n1(r.omsorgsbustad_andel_80)} %`],
    ],
    kjelde: ["11875", "KOSTRA 11875"],
  },
  {
    key: "kultur_per_100_barn",
    testId: "kulturskuleplassar",
    icon: Music,
    title: "Kulturskuleplassar per 100 barn 6–18 år",
    unit: "plassar",
    farge: "#B5832B",
    forklaring: "Elevplassar i kommunens kulturskule målt mot talet på barn og unge 6–18 år. Éin elev kan ha fleire plassar.",
    detalj: (r) => [
      ["Elevplassar", formatNumber(r.kulturskuleplassar)],
      ["Elevar", formatNumber(r.kulturskuleelevar)],
      ["Barn 6–18 år", formatNumber(r.barn_6_18)],
    ],
    kjelde: ["14523", "KOSTRA 14523"],
  },
];

const BEMANNING = [
  {
    key: "barn_per_arsverk",
    testId: "barn-per-arsverk",
    icon: Baby,
    title: "Barn per årsverk i barnehage",
    unit: "barn",
    farge: "#103766",
    forklaring:
      "Barn korrigert for alder per årsverk til grunnbemanning, alle barnehagar. Barn under tre år tel som to, slik bemanningsnorma gjer. Norma er maks 6,0.",
    detalj: (r) => [
      ["Barn med plass", formatNumber(r.barnehagebarn)],
      ["Årsverk til grunnbemanning", n1(r.barnehage_arsverk)],
      ["Årsverk i alt", n1(r.barnehage_arsverk_alle)],
      ["Barnehagelærarar av grunnbemanninga", r.barnehagelarar_andel == null ? "—" : `${n1(r.barnehagelarar_andel)} %`],
      ["Bemanningsnorm", "6,0 barn"],
      ["Pedagognorm", "43 %"],
    ],
    kjelde: ["13502", "KOSTRA 13502"],
    norm: BEMANNING_NORM.barn_per_arsverk,
    domain: [4, 7],
  },
  {
    key: "elevar_per_laerararsverk",
    testId: "elevar-per-arsverk",
    icon: School,
    title: "Elevar per lærarårsverk",
    unit: "elevar",
    farge: "#3F8A86",
    forklaring:
      "Elevar i kommunale grunnskular delt på avtalte lærarårsverk. Den stipla linja viser elevar per årsverk når alle tilsette i grunnskulesektoren er med, og dei lyse linjene viser tala når lange fråvær er trekte frå – gapet er bemanninga som ikkje er på jobb.",
    ekstra: [
      { key: "elevar_per_laerararsverk_eks", label: "Per lærarårsverk utan lange fråvær", farge: "#C0392B", dash: "4 3" },
      { key: "elevar_per_skulearsverk", label: "Per årsverk i heile sektoren", farge: "#8AA4BF", dash: "2 3" },
      { key: "elevar_per_skulearsverk_eks", label: "Per årsverk utan lange fråvær", farge: "#D9C7A8", dash: "2 3" },
    ],
    detalj: (r) => [
      ["Elevar", formatNumber(r.elevar)],
      ["Lærarårsverk", n1(r.laerararsverk)],
      ["Lærarårsverk utan lange fråvær", n1(r.laerararsverk_eks)],
      ["Årsverk i sektoren", n1(r.skule_arsverk)],
      ["Av dei assistentårsverk", n1(r.assistentarsverk)],
      ["Elevar per årsverk i sektoren", n1(r.elevar_per_skulearsverk)],
    ],
    kjelde: ["11981", "SSB 11981"],
    hoyde: 200,
  },
  {
    key: "brukarar_per_arsverk_omsorg",
    testId: "brukarar-per-arsverk",
    icon: HeartPulse,
    title: "Brukarar per årsverk i omsorgstenesta",
    unit: "brukarar",
    farge: "#C0392B",
    desimalar: 2,
    aksedesimalar: 1,
    domain: ["dataMin - 0.15", "dataMax + 0.15"],
    forklaring:
      "Brukarar av omsorgstenester per årsverk i tenesta, rekna om frå SSB sitt nøkkeltal «årsverk per brukar». Høgt tal tyder fleire brukarar på kvart årsverk.",
    detalj: (r) => [
      ["Årsverk per brukar", r.omsorg_arsverk_per_brukar == null ? "—" : dec(r.omsorg_arsverk_per_brukar, 2)],
      ["Brukarar av heimetenester", formatNumber(r.brukarar_heimeteneste)],
      ["Brukarar av institusjonstenester", formatNumber(r.brukarar_institusjon)],
      ["Årsverk i helse og omsorg", formatNumber(r.omsorg_arsverk)],
      ["Med helsefagleg utdanning", r.omsorg_helsefag_andel == null ? "—" : `${n1(r.omsorg_helsefag_andel)} %`],
      ["Innbyggjarar 80+", formatNumber(r.eldre_80)],
    ],
    kjelde: ["12209", "KOSTRA 12209"],
  },
];

const BRUKAR_SERIER = [
  { key: "barnehagebarn", label: "Barn i barnehage", farge: "#103766" },
  { key: "elevar", label: "Elevar i kommunal skule", farge: "#3F8A86" },
  { key: "brukarar_heimeteneste", label: "Brukarar av heimetenester", farge: "#C0392B" },
  { key: "brukarar_institusjon", label: "Brukarar av institusjonstenester", farge: "#B5832B" },
];

const BrukartalChart = ({ history }) => {
  const chart = history.map((r) => ({
    year: r.year,
    ...Object.fromEntries(BRUKAR_SERIER.map((x) => [x.label, r[x.key] ?? null])),
  }));
  const har = BRUKAR_SERIER.filter((x) => chart.some((r) => r[x.label] != null));
  if (har.length === 0) return null;
  return (
    <div className="p-4 border border-[#E6E9F0] rounded-xl space-y-3 lg:col-span-2" data-testid="brukartal-block">
      <div className="flex items-start gap-2">
        <Users className="w-4 h-4 text-[#8AA4BF] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Kor mange brukar tenestene</p>
          <p className="text-xs text-[#8AA4BF] mt-0.5">
            Talet på barn, elevar og mottakarar av omsorgstenester i kommunen – nemnaren bak bemanningstala over. Ein person kan ta imot både
            heimetenester og institusjonstenester, så dei to omsorgsseriane kan overlappe.
          </p>
        </div>
      </div>
      <div className="h-[260px]" data-testid="brukartal-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
            {endringsLinjer(chart)}
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
            <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
            <YAxis stroke="#8AA4BF" fontSize={11} width={54} tickFormatter={(v) => formatNumber(v)} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatNumber(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
            {har.map((x) => (
              <Line key={x.key} type="monotone" dataKey={x.label} stroke={x.farge} strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-[#8AA4BF]">
        Kjelder: SSB 14522 (barn i barnehage), 11971 (elevar i kommunale grunnskular) og 11645 (brukarar av omsorgstenester per 31.12.).
      </p>
    </div>
  );
};

const BemanningBlokk = ({ data }) => {
  if (!BEMANNING.some((k) => data.latest?.[k.key] != null)) return null;
  return (
    <div className="lg:col-span-2 space-y-3" data-testid="bemanning-block">
      <div>
        <p className="text-sm font-medium text-[#1F2A36]">Bemanning: kor mange brukarar det er per årsverk</p>
        <p className="text-xs text-[#8AA4BF]">
          Dei tre tala folk kjenner frå kuttdebatten – barn per vaksen i barnehagen, elevar per lærar og brukarar per årsverk i eldreomsorga – med{" "}
          {data.gruppe_namn ? "storby- og landssnitt og plassering i storbygruppa" : "fylkes- og landssnitt og plassering i fylket"}.
        </p>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {BEMANNING.map((kort) => (
          <Kort key={kort.key} kort={kort} data={data} />
        ))}
      </div>
    </div>
  );
};


const Kort = ({ kort, data }) => {
  const nd = (v) => (v == null ? "—" : dec(v, kort.desimalar ?? 1));
  const { latest, land, history, land_history, rangering, fylke_name } = data;
  const fylke = data.gruppe || data.fylke;
  const fylke_history = data.gruppe_history || data.fylke_history;
  const gruppeNamn = data.gruppe_namn ? "Storbyane" : fylke_name;
  const verdi = latest?.[kort.key];
  const fylkeVerdi = fylke?.[kort.key];
  const landVerdi = land?.[kort.key];
  const rang = rangering?.[kort.key];
  const Icon = kort.icon;
  const chart = history.map((r) => ({
    year: r.year,
    Kommunen: r[kort.key],
    ...Object.fromEntries((kort.ekstra || []).map((e) => [e.label, r[e.key] ?? null])),
    ...(fylke_history?.length ? { [gruppeNamn || "Fylket"]: fylke_history.find((f) => f.year === r.year)?.[kort.key] ?? null } : {}),
    Landet: land_history?.find((f) => f.year === r.year)?.[kort.key] ?? null,
  }));

  return (
    <div className="p-4 border border-[#E6E9F0] rounded-xl space-y-3" data-testid={`tjeneste-${kort.testId}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 text-[#8AA4BF] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">{kort.title}</p>
          <p className="text-xs text-[#8AA4BF] mt-0.5">{kort.forklaring}</p>
        </div>
      </div>
      <div className="flex items-end gap-5">
        <div>
          <p className="text-3xl font-light text-[#1F2A36]" data-testid={`${kort.testId}-verdi`}>
            {nd(verdi)} <span className="text-sm text-[#8AA4BF]">{kort.unit}</span>
          </p>
          <p className="text-[11px] text-[#4B647F]">{latest?.year}</p>
        </div>
        <div className="text-xs text-[#4B647F] space-y-0.5" data-testid={`${kort.testId}-snitt`}>
          {fylkeVerdi != null && (
            <p>
              {gruppeNamn || "Fylket"}: <span className="text-[#3F8A86] font-medium">{nd(fylkeVerdi)}</span>
            </p>
          )}
          <p>
            Landet: <span className="text-[#103766] font-medium">{nd(landVerdi)}</span>
          </p>
        </div>
      </div>
      {rang && (
        <p className="text-xs text-[#4B647F]" data-testid={`${kort.testId}-rangering`}>
          Nr. <span className="font-semibold text-[#103766]">{rang.own_rank}</span> av {rang.count} i {data.gruppe_namn ? "storbygruppa" : "fylket"} (høgast: {rang.hoegast.name}{" "}
          {nd(rang.hoegast.value)} · lågast: {rang.lagast.name} {nd(rang.lagast.value)})
          {rang.hoeg_er_best === false ? " – lågt tal tyder betre bemanning" : ""}
        </p>
      )}
      <div className="grid grid-cols-3 gap-2">
        {kort.detalj(latest || {}).map(([label, value]) => (
          <div key={label} className="bg-[#F7F9FC] rounded-lg px-2 py-1.5">
            <p className="text-[10px] text-[#8AA4BF] leading-tight">{label}</p>
            <p className="text-sm text-[#1F2A36] tabular-nums">{value}</p>
          </div>
        ))}
      </div>
      <div style={{ height: kort.hoyde || 140 }} data-testid={`${kort.testId}-chart`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            {endringsLinjer(chart)}
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
            <XAxis dataKey="year" stroke="#8AA4BF" fontSize={10} />
            <YAxis stroke="#8AA4BF" fontSize={10} width={38} domain={kort.domain || ["dataMin - 1", "dataMax + 1"]} tickFormatter={(v) => dec(v, kort.aksedesimalar ?? 0)} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => nd(v)} />
            <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
            <Line type="monotone" dataKey="Kommunen" stroke={kort.farge} strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls />
            {(kort.ekstra || []).map((e) => (
              <Line key={e.key} type="monotone" dataKey={e.label} stroke={e.farge} strokeWidth={1.5} strokeDasharray={e.dash} dot={false} connectNulls />
            ))}
            {fylke_history?.length > 0 && (
              <Line type="monotone" dataKey={gruppeNamn || "Fylket"} stroke="#3F8A86" strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
            )}
            <Line type="monotone" dataKey="Landet" stroke="#9FB6CC" strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls />
            {kort.norm != null && (
              <ReferenceLine y={kort.norm} stroke="#C0392B" strokeDasharray="5 4"
                label={{ value: `Norm ${dec(kort.norm, 1)}`, position: "insideTopRight", fontSize: 9, fill: "#C0392B" }} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const GAP = {
  eldre: {
    testId: "eldrebolge",
    tittel: "Eldrebølgja møter plassane",
    gruppe: "innbyggjarar 80 år og over",
    eining: "sjukeheimsplassane og omsorgsbustadene",
    serie: "Innbyggjarar 80+",
    farge: "#9FB6CC",
    per: "per 100 over 80 år",
    kjelde: "Den raude stipla linja er dagens tal på plassar og omsorgsbustader til saman. Framskrivinga er SSB sitt hovudalternativ (tabell 14288). Plassane er haldne konstante med vilje – poenget er å vise gapet som veks dersom kommunen ikkje byggjer ut.",
  },
  barnehage: {
    testId: "barnehagebolge",
    tittel: "Barnehagen møter barna",
    gruppe: "barn 1–5 år",
    eining: "barnehageplassane",
    serie: "Barn 1–5 år",
    farge: "#C8D8C4",
    per: "per 100 barn",
    kjelde: "Den raude stipla linja er dagens tal på plassar. Framskrivinga er SSB sitt hovudalternativ (tabell 14288), og talet på barn med plass kjem frå SSB 14522. Dekninga her er barn med plass delt på innbyggjarar 1–5 år i kommunen, og kan avvike litt frå SSB sitt offisielle dekningstal fordi barn kan ha plass i ein annan kommune.",
  },
};

const GapBlock = ({ e, kommuneName, type }) => {
  const k = GAP[type];
  const chart = [
    { year: e.basisaar, [k.serie]: e.folk_i_dag, "Plassar for same dekning": e.plassar, dekning: e.dekning_i_dag },
    ...e.rows.map((r) => ({
      year: r.year,
      [k.serie]: r.folk,
      "Plassar for same dekning": r.plassar_for_same_dekning,
      dekning: r.dekning_med_dagens_plassar,
    })),
  ];
  const vekst = e.vekst_pct ?? 0;
  const mangel = e.siste.mangel;
  return (
    <div className="p-4 border border-[#E6E9F0] rounded-xl space-y-3 lg:col-span-2" data-testid={k.testId}>
      <div className="flex items-start gap-2">
        <TrendingUp className="w-4 h-4 text-[#8AA4BF] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">{k.tittel}</p>
          <p className="text-xs text-[#8AA4BF] mt-0.5" data-testid={`${k.testId}-tekst`}>
            Talet på {k.gruppe} i {kommuneName} {vekst >= 0 ? "veks" : "fell"} frå {formatNumber(e.folk_i_dag)} i {e.basisaar} til{" "}
            {formatNumber(e.siste.folk)} i {e.siste.year} – ei endring på {n1(vekst)} %. Held kommunen dei {formatNumber(e.plassar)}{" "}
            {k.eining} ho har i dag, {e.siste.dekning_med_dagens_plassar < e.dekning_i_dag ? "fell" : "stig"} dekninga frå {n1(e.dekning_i_dag)} til{" "}
            {n1(e.siste.dekning_med_dagens_plassar)} plassar {k.per}. For å halde dagens dekning trengst{" "}
            {formatNumber(e.siste.plassar_for_same_dekning)} plassar –{" "}
            <span className={`font-semibold ${mangel > 0 ? "text-[#C0392B]" : "text-[#3F8A86]"}`}>
              {mangel > 0 ? `${formatNumber(mangel)} fleire enn i dag` : `${formatNumber(Math.abs(mangel))} færre enn i dag`}
            </span>
            .
          </p>
        </div>
      </div>
      <div className="h-[260px]" data-testid={`${k.testId}-chart`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
            <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
            <YAxis yAxisId="a" stroke="#8AA4BF" fontSize={11} width={54} tickFormatter={(v) => formatNumber(v)} />
            <YAxis yAxisId="p" orientation="right" stroke="#103766" fontSize={11} width={48} tickFormatter={(v) => formatNumber(v)} domain={[0, (max) => Math.round(max * 1.25)]} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n, pl) => (n === "Plassar for same dekning" ? [`${formatNumber(v)} plassar (dekning med dagens plassar: ${n1(pl.payload.dekning)} ${k.per})`, n] : [formatNumber(v), n])} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
            <Bar yAxisId="a" dataKey={k.serie} fill={k.farge} radius={[4, 4, 0, 0]} />
            <Line yAxisId="p" type="monotone" dataKey="Plassar for same dekning" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} />
            <ReferenceLine yAxisId="p" y={e.plassar} stroke="#C0392B" strokeDasharray="5 4" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-[#8AA4BF]">{k.kjelde}</p>
    </div>
  );
};

const SkuleBlock = ({ e, kommuneName }) => {
  const chart = [
    { year: e.basisaar, "Barn 6–15 år": e.folk_i_dag, Elevar: e.elevar, "Elevar per skule": e.elevar_per_skule },
    ...e.rows.map((r) => ({
      year: r.year,
      "Barn 6–15 år": r.folk,
      Elevar: r.elevar,
      "Elevar per skule": r.elevar_per_skule_med_dagens_skular,
    })),
  ];
  const faerre = e.siste.endring_elevar < 0;
  const skuleEndring = e.endring_skular;
  return (
    <div className="p-4 border border-[#E6E9F0] rounded-xl space-y-3 lg:col-span-2" data-testid="skulebolge">
      <div className="flex items-start gap-2">
        <TrendingUp className="w-4 h-4 text-[#8AA4BF] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Skulane møter elevane</p>
          <p className="text-xs text-[#8AA4BF] mt-0.5" data-testid="skulebolge-tekst">
            Talet på barn 6–15 år i {kommuneName} {e.vekst_pct >= 0 ? "veks" : "fell"} frå {formatNumber(e.folk_i_dag)} i {e.basisaar} til{" "}
            {formatNumber(e.siste.folk)} i {e.siste.year} – ei endring på {n1(e.vekst_pct)} %. Med same del av barna i kommunal skule ({n1(e.andel_i_kommunal_skule)} %)
            blir det {formatNumber(e.siste.elevar)} elevar, {faerre ? "ned" : "opp"} {formatNumber(Math.abs(e.siste.endring_elevar))} frå{" "}
            {formatNumber(e.elevar)} i dag. Held kommunen dei {e.skular} skulane ho har, fell snittet frå {n1(e.elevar_per_skule)} til{" "}
            {n1(e.siste.elevar_per_skule_med_dagens_skular)} elevar per skule. For å halde dagens skulestorleik trengst{" "}
            <span className={`font-semibold ${skuleEndring < 0 ? "text-[#C0392B]" : "text-[#3F8A86]"}`}>
              {n1(e.siste.skular_for_same_storleik)} skular – {skuleEndring === 0 ? "like mange som i dag" : `${n1(Math.abs(skuleEndring))} ${skuleEndring < 0 ? "færre" : "fleire"} enn i dag`}
            </span>
            .
          </p>
        </div>
      </div>
      <div className="h-[260px]" data-testid="skulebolge-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
            <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
            <YAxis yAxisId="a" stroke="#8AA4BF" fontSize={11} width={54} tickFormatter={(v) => formatNumber(v)} />
            <YAxis yAxisId="p" orientation="right" stroke="#3F8A86" fontSize={11} width={48} tickFormatter={(v) => dec(v, 0)} domain={[0, (max) => Math.round(max * 1.3)]} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n === "Elevar per skule" ? [`${n1(v)} elevar per skule`, n] : [formatNumber(v), n])} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
            <Bar yAxisId="a" dataKey="Barn 6–15 år" fill="#D9C7A8" radius={[4, 4, 0, 0]} />
            <Line yAxisId="a" type="monotone" dataKey="Elevar" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line yAxisId="p" type="monotone" dataKey="Elevar per skule" stroke="#3F8A86" strokeWidth={2} strokeDasharray="4 3" dot={false} />
            <ReferenceLine yAxisId="p" y={e.elevar_per_skule} stroke="#C0392B" strokeDasharray="5 4" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-[#8AA4BF]">
        Den raude stipla linja er dagens snitt på {n1(e.elevar_per_skule)} elevar per skule. Elevtalet er framskrive ved å halde dagens del av barna i
        kommunal skule konstant (resten går i private skular eller i ein annan kommune). Framskrivinga er SSB sitt hovudalternativ (tabell 14288), og
        skule- og elevtala kjem frå SSB 11971.
      </p>
    </div>
  );
};

export const TjenesterSection = ({ data, kommuneCode }) => {
  if (!data?.latest) return null;
  return (
    <Card className="border-[#E6E9F0]" data-testid="tjenester-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base md:text-lg font-normal text-[#1F2A36]">Tenester til barn, unge og eldre</CardTitle>
          {kommuneCode && <DelKnapp kommuneCode={kommuneCode} kommuneNamn={data.kommune_name} tema="bemanning" testId="del-bemanning" />}
        </div>
        <p className="text-sm text-[#4B647F]">
          Kor mange plassar og kor store einingar kommunen har, og kor mange brukarar det er per årsverk, målt mot dei som skal bruke tenestene – med{" "}
          {data.gruppe_namn
            ? "storby- og landssnitt og plassering i storbygruppa"
            : data.eige_fylke
              ? "landssnitt (Oslo er både kommune og fylke)"
              : "fylkes- og landssnitt og plassering i fylket"}.
        </p>
      </CardHeader>
      <CardContent className="grid lg:grid-cols-2 gap-4">
        <BemanningBlokk data={data} />
        <Normbrot data={data} />
        <BemanningEndring data={data} />
        <Kuttkalkulator data={data} />
        <BrukartalChart history={data.history} />
        {KORT.map((kort) => (
          <Kort key={kort.key} kort={kort} data={data} />
        ))}
        {data.barnehagebolge && <GapBlock e={data.barnehagebolge} kommuneName={data.kommune_name} type="barnehage" />}
        {data.skulebolge && <SkuleBlock e={data.skulebolge} kommuneName={data.kommune_name} />}
        {data.eldrebolge && <GapBlock e={data.eldrebolge} kommuneName={data.kommune_name} type="eldre" />}
        <p className="text-xs text-[#8AA4BF] lg:col-span-2">
          Bemanningstala kjem frå KOSTRA{" "}
          <a href="https://www.ssb.no/statbank/table/11981" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            11981 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (årsverk i grunnskulesektoren),{" "}
          <a href="https://www.ssb.no/statbank/table/12209" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12209 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (årsverk per brukar i omsorgstenesta) og{" "}
          <a href="https://www.ssb.no/statbank/table/11645" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            11645 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          (brukarar av omsorgstenester). «Utan lange fråvær» er avtalte årsverk minus sjukmeldingar og permisjonar over eitt år – differansen er
          stillingar kommunen har, men som ikkje er på jobb. Snitta for{" "}
          {data.gruppe_namn ? "storbyane" : "fylket"} og landet er rekna av summen av elevar og årsverk, så store kommunar veg mest. Kjelder: SSB{" "}
          {KORT.map((k, i) => (
            <span key={k.key}>
              {i > 0 ? ", " : ""}
              <a href={`https://www.ssb.no/statbank/table/${k.kjelde[0]}`} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
                {k.kjelde[0]} <ExternalLink className="w-3 h-3" />
              </a>
            </span>
          ))}
          ,{" "}
          <a href="https://www.ssb.no/statbank/table/04469" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            04469 <ExternalLink className="w-3 h-3" />
          </a>
          . Skule- og elevtal gjeld kommunale grunnskular. Befolkningstala er per 1. januar i same år (SSB 07459), og barnehagedekninga kan passere
          100 prosent i kommunar som tar imot barn frå nabokommunar. Statistikkbanken har ikkje tal på sjølve omsorgsbustadene, så talet på
          omsorgsbustader her er bebuarar i bustader kommunen disponerer til pleie- og omsorgsformål (SSB 04469). Dei bur der i alle aldrar – ikkje
          berre dei eldste – og kortet viser kor stor del av dei som er 80 år og over.
        </p>
      </CardContent>
    </Card>
  );
};

export default TjenesterSection;
